import { Fragment, type ReactElement, type ReactNode } from 'react';
import { t } from '@/messages/zh-TW';

export interface TableColumn {
  key: string;
  /** 表頭文字；批次 15（B7-3）：可給元素（arrange-train 完成份數欄頭＝openFormList 連結）。 */
  header: string | ReactElement;
  align?: 'left' | 'center' | 'right';
  /** 批次 43（#41）：複雜表頭合併儲存格（commonFormTemplate.jsp:250-253 rowspan=2/colspan=3）。 */
  rowSpan?: number;
  colSpan?: number;
}

export type TableRow = Record<string, unknown>;

export interface TableProps {
  uiId: string;
  columns: readonly TableColumn[];
  rows: readonly TableRow[];
  /**
   * 回傳這一列的穩定 key。同時會被拿去組資料列的 data-ui-id，
   * 所以請回傳 kebab-case 的固定 mock fixture key（§10.1），不要回傳使用者輸入值。
   */
  rowKey: (row: TableRow, index: number) => string;
  /** 不給就直接把儲存格的值轉字串。要放按鈕、連結、格式化數字時才需要自訂。 */
  renderCell?: (row: TableRow, columnKey: string) => ReactNode;
  /** 不給就用固定文字 key `table.empty`（§17）。 */
  emptyText?: string;
  /**
   * 批次 15（B7-1，Gitea issue #13）：列展開的子內容（legacy DataTable `row.child()`；
   * planInstList 明細 details-control，planInstList_js.jsp:133-145）。受控模式：
   * 由呼叫端（CrudListPage→PageBehavior.rowExpanded）提供展開狀態與內容；
   * 未提供 expandedRowRender＝無展開行為，未登錄頁零影響。
   */
  expandedRowRender?: (row: TableRow) => ReactNode;
  /** 目前展開的列鍵集合（受控）；鍵＝rowKey 輸出空間。 */
  expandedKeys?: ReadonlySet<string>;
  /**
   * 批次 43（#41）：複雜表頭第二列（commonFormTemplate.jsp:255-259：醫院/職類/部門）。
   * 未傳＝單列表頭（既有行為，零影響）。
   */
  secondHeaderRow?: readonly { header: string; align?: 'left' | 'center' | 'right' }[];
  /**
   * 批次 43（#41）：複雜表頭第一列（含 rowSpan/colSpan 合併儲存格；與 columns 分離——
   * tbody 仍照 columns 全量渲染）。未傳＝thead 照 columns（既有行為，零影響）。
   */
  headerColumns?: readonly TableColumn[];
}

/** 靜態對照表（§8.1：不得用樣板字串動態組 Tailwind class）。 */
const ALIGN_CLASS: Record<'left' | 'center' | 'right', string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

/**
 * 原系統表格外觀（§7 第 3 級，【雛形值待實機校準】）：
 *   `.table { width:100%; max-width:100%; margin-bottom:20px }`（bootstrap.css:2264-2268）
 *   `table { border-collapse:collapse; border-spacing:0 }`（bootstrap.css:182-185，Tailwind preflight
 *     沒有這一條，所以要自己加 border-collapse，否則儲存格邊框會變雙線）
 *   儲存格 `padding:18px; line-height:1.42857; border-top:1px solid #e7eaec`（css/style.css:3502-3510）
 *   表頭 `border-bottom:1px solid #DDDDDD; vertical-align:bottom`（css/style.css:3498-3501）
 */
const TABLE_CLASS = 'mb-5 w-full max-w-full border-collapse text-ink';
const CELL_CLASS = 'border-t border-border p-[18px] leading-[1.42857]';
const HEAD_CELL_CLASS = 'border-b border-border-strong p-[18px] align-bottom font-bold';

function defaultCell(row: TableRow, columnKey: string): ReactNode {
  const value = row[columnKey];
  return value === null || value === undefined ? '' : String(value);
}

/**
 * 基礎表格結構：只有 thead／tbody／空資料列。
 *
 * ponytail: 分頁、查詢、排序、每頁筆數、匯出 Excel 這些 chrome 全部不做 —— 那是第二層
 * DataTableShell 的職責，做進來會讓每個只想印一張靜態表的地方都被迫帶著整套 DataTables。
 * 排序也不做：原系統的排序是 DataTables 送 request 回 server，那是資料層的事，不是這一層。
 *
 * data-ui-id：根節點、表頭列、每一筆資料列、空資料列各一個。資料列用 rowKey 延伸
 * （§10.1 的 instance ID 規則）。儲存格本身不配置 —— 儲存格內真正可互動的東西
 * （按鈕、連結）由呼叫端在 renderCell 裡自行配置自己的 ID。
 */
export function Table({
  uiId,
  columns,
  rows,
  rowKey,
  renderCell,
  emptyText,
  expandedRowRender,
  expandedKeys,
  secondHeaderRow,
  headerColumns,
}: TableProps) {
  // GNL-001（批次 28，Gitea issue #29）：表頭 <th> 不帶 scope 屬性。legacy 頁面表頭無 scope
  // （structure-diff A:th:scope:null→col 344 筆，56 頁）；早年為 a11y 加的 scope="col" 已裁決移除，
  // 鏡像 legacy。legacy 側僅存的 scope 全是 jquery-ui datepicker 萬年曆表頭（ui-datepicker-week-end），
  // 非頁面表頭、不在比對範圍。
  return (
    <table data-ui-id={uiId} className={TABLE_CLASS}>
      <thead>
        <tr data-ui-id={`${uiId}-head`}>
          {(headerColumns ?? columns).map((column) => (
            <th
              key={column.key}
              className={`${HEAD_CELL_CLASS} ${ALIGN_CLASS[column.align ?? 'left']}`}
              {...(column.rowSpan === undefined ? {} : { rowSpan: column.rowSpan })}
              {...(column.colSpan === undefined ? {} : { colSpan: column.colSpan })}
            >
              {column.header}
            </th>
          ))}
        </tr>
        {secondHeaderRow === undefined ? null : (
          <tr data-ui-id={`${uiId}-head-2`}>
            {secondHeaderRow.map((cell, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: 第二列表頭是靜態文字序列，無排序/增刪。
              <th key={index} className={`${HEAD_CELL_CLASS} ${ALIGN_CLASS[cell.align ?? 'left']}`}>
                {cell.header}
              </th>
            ))}
          </tr>
        )}
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr data-ui-id={`${uiId}-empty`}>
            <td colSpan={columns.length} className={`${CELL_CLASS} text-center text-ink-muted`}>
              {emptyText ?? t('table.empty')}
            </td>
          </tr>
        ) : (
          rows.map((row, index) => {
            const key = rowKey(row, index);
            const expanded =
              expandedRowRender !== undefined &&
              expandedKeys !== undefined &&
              expandedKeys.has(key);
            return (
              <Fragment key={key}>
                <tr data-ui-id={`${uiId}-row-${key}`}>
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={`${CELL_CLASS} ${ALIGN_CLASS[column.align ?? 'left']}`}
                    >
                      {renderCell ? renderCell(row, column.key) : defaultCell(row, column.key)}
                    </td>
                  ))}
                </tr>
                {expanded && expandedRowRender !== undefined ? (
                  <tr data-ui-id={`${uiId}-row-${key}-detail`}>
                    <td colSpan={columns.length} className={`${CELL_CLASS} p-0`}>
                      {expandedRowRender(row)}
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })
        )}
      </tbody>
    </table>
  );
}
